// Invoice approval service — auto-approve pending invoices against approval rules.
// Pure rule matching + threshold evaluation. Uses InvoiceDao for data access.

import { InvoiceDao } from './InvoiceDao.ts';
import { monthOf, firstOfNextMonth } from '../../calc.ts';
import { isInvoicePaid } from './paymentState.ts';
import { findWarningClauses } from './warningClauses.ts';
import type { InvoiceRow } from '@shared/types/invoice.ts';
import type { ReconcileReport, ReconcileViolation } from '@shared/reconcile.ts';
import type { PaymentMethod } from '@shared/types.ts';
import { errorText } from '../../errorText.ts';

// ── Types ────────────────────────────────────────────────────────────────

export interface ApprovalRule {
  id: number;
  name: string;
  priority: number;
  is_active: boolean;
  action: 'auto_approve' | 'suggest_approval';
  /** When true, the rule only fires for invoices that are already paid (bank or FA).
   *  Used for overhead vendors we auto-approve once payment is confirmed. */
  require_paid: boolean;
  vendor_company_ids: number[];
  allowed_currencies: string[];
  /** DB CHECK constraint pins this to the PaymentMethod union (or NULL). */
  override_payment_method: PaymentMethod | null;
  expected_line_item_descriptions: string[] | null;
  max_invoice_net_amount: number | null;
  min_invoices_per_month: number | null;
  max_invoices_per_month: number | null;
  max_total_net_per_month: number | null;
  allowed_vat_rate_codes: string[] | null;
}

interface ApprovalCheckResult {
  status: 'ok' | 'failed' | 'skipped';
  [k: string]: unknown;
}

interface MonthlyAggregates {
  count: number;
  totalNet: number;
}

export interface ApprovalResult {
  approved: number;
  suggested: number;
  skipped: number;
  failed: number;
}

// ── Pure rule matching ──────────────────────────────────────────────────

function findMatchingRuleImpl(
  rules: ApprovalRule[],
  vendorId: number,
  currency: string,
  invoicePaid: boolean,
): ApprovalRule | null {
  for (const rule of rules) {
    if (!rule.is_active) continue;
    // A paid-only rule is NOT a match for an unpaid invoice — fall through to the next
    // rule (so a higher-priority require_paid rule doesn't shadow a lower-priority
    // capped rule for the same vendor when the invoice isn't paid yet).
    if (rule.require_paid && !invoicePaid) continue;
    if (rule.vendor_company_ids.length > 0 && !rule.vendor_company_ids.includes(vendorId)) continue;
    if (rule.allowed_currencies.length > 0 && !rule.allowed_currencies.includes(currency)) continue;
    return rule;
  }
  return null;
}

// ── Pure threshold evaluation ───────────────────────────────────────────

function evaluateRuleImpl(
  rule: ApprovalRule,
  invoice: { net_amount: number | null; currency: string },
  lineItems: { description: string | null; name: string | null; vat_rate_code: string | null }[],
  monthlyAgg: MonthlyAggregates,
): { allowed: boolean; checks: Record<string, ApprovalCheckResult>; failedChecks: string[] } {
  const checks: Record<string, ApprovalCheckResult> = {};
  const failedChecks: string[] = [];

  if (rule.max_invoice_net_amount != null) {
    const actual = Math.abs(invoice.net_amount ?? 0);
    const ok = actual <= rule.max_invoice_net_amount;
    checks.max_invoice_net_amount = { status: ok ? 'ok' : 'failed', limit: rule.max_invoice_net_amount, actual };
    if (!ok) failedChecks.push('max_invoice_net_amount');
  } else {
    checks.max_invoice_net_amount = { status: 'skipped' };
  }

  if (rule.min_invoices_per_month != null || rule.max_invoices_per_month != null) {
    const count = monthlyAgg.count + 1;
    const min = rule.min_invoices_per_month ?? 0;
    const max = rule.max_invoices_per_month ?? Infinity;
    const ok = count >= min && count <= max;
    checks.monthly_count = { status: ok ? 'ok' : 'failed', range: [min, max === Infinity ? null : max], actual: count };
    if (!ok) failedChecks.push('monthly_count');
  } else {
    checks.monthly_count = { status: 'skipped' };
  }

  if (rule.max_total_net_per_month != null) {
    const totalAfter = monthlyAgg.totalNet + Math.abs(invoice.net_amount ?? 0);
    const ok = totalAfter <= rule.max_total_net_per_month;
    checks.max_total_net_per_month = { status: ok ? 'ok' : 'failed', limit: rule.max_total_net_per_month, actual: totalAfter };
    if (!ok) failedChecks.push('max_total_net_per_month');
  } else {
    checks.max_total_net_per_month = { status: 'skipped' };
  }

  const allowedVatRateCodes = rule.allowed_vat_rate_codes;
  if (allowedVatRateCodes != null && allowedVatRateCodes.length > 0) {
    const found = [...new Set(lineItems.map(li => li.vat_rate_code).filter(Boolean))] as string[];
    const disallowed = found.filter(v => !allowedVatRateCodes.includes(v));
    const ok = disallowed.length === 0;
    checks.vat_rate_codes = { status: ok ? 'ok' : 'failed', allowed: allowedVatRateCodes, found, disallowed };
    if (!ok) failedChecks.push('vat_rate_codes');
  } else {
    checks.vat_rate_codes = { status: 'skipped' };
  }

  if (rule.expected_line_item_descriptions != null && rule.expected_line_item_descriptions.length > 0) {
    let patterns: RegExp[];
    try {
      patterns = rule.expected_line_item_descriptions.map(p => new RegExp(p, 'i'));
    } catch (e: unknown) {
      checks.line_item_descriptions = { status: 'failed', error: `Invalid regex: ${errorText(e) ?? String(e)}` };
      failedChecks.push('line_item_descriptions');
      return { allowed: false, checks, failedChecks };
    }
    const unmatched: string[] = [];
    for (const li of lineItems) {
      const text = li.name || li.description || '';
      if (!patterns.some(p => p.test(text))) unmatched.push(text);
    }
    const ok = unmatched.length === 0;
    checks.line_item_descriptions = { status: ok ? 'ok' : 'failed', patterns: rule.expected_line_item_descriptions, unmatched };
    if (!ok) failedChecks.push('line_item_descriptions');
  } else {
    checks.line_item_descriptions = { status: 'skipped' };
  }

  return { allowed: failedChecks.length === 0, checks, failedChecks };
}

// ── Report builder ──────────────────────────────────────────────────────

function buildApprovalReport(
  result: 'auto_approved' | 'suggest_approval' | 'partial',
  evalResult: { ruleId: number; ruleName: string; checks: Record<string, ApprovalCheckResult>; failedChecks: string[] },
  extraViolations: ReconcileViolation[] = [],
): unknown {
  const violations: unknown[] = [];
  for (const check of evalResult.failedChecks) {
    violations.push({ type: 'approval_rule_blocked', details: { check, ...evalResult.checks[check] } });
  }
  violations.push(...extraViolations);
  return {
    timestamp: new Date().toISOString(),
    result,
    line_items: [],
    total_check: null,
    violations,
    approval_rule: { rule_id: evalResult.ruleId, rule_name: evalResult.ruleName, checks: evalResult.checks },
  };
}

// ── Service ─────────────────────────────────────────────────────────────

function getVendorId(inv: InvoiceRow): number {
  return inv.direction === 'received' ? inv.seller_company_id : inv.buyer_company_id;
}

/** Paid per the app (bank-driven) OR Fakturownia. Delegates to the shared
 *  isInvoicePaid predicate so the approval and payment layers agree. */
function isPaid(inv: InvoiceRow): boolean {
  return isInvoicePaid(inv);
}

export class InvoiceApprovalService {
  /** Pure rule matching — first active rule whose vendor/currency/paid gates all pass. */
  static readonly findMatchingRule = findMatchingRuleImpl;
  /** Pure threshold evaluation — per-check results + the failed-check list for one rule. */
  static readonly evaluateRule = evaluateRuleImpl;

  constructor(private readonly dao: InvoiceDao) {}

  /** Auto-approve pending invoices against approval rules. */
  async autoApproveInvoices(invoiceId?: number): Promise<ApprovalResult> {
    console.log(`[auto-approve] starting${invoiceId ? ` for invoice ${invoiceId}` : ' (all pending)'}`);

    const invoices = await this.dao.getPendingForApproval(invoiceId);
    if (invoices.length === 0) { console.log('[auto-approve] no pending invoices'); return { approved: 0, suggested: 0, skipped: 0, failed: 0 }; }

    const rules = await this.dao.getApprovalRules() as ApprovalRule[];
    if (rules.length === 0) { console.log('[auto-approve] no active rules'); return { approved: 0, suggested: 0, skipped: invoices.length, failed: 0 }; }

    const invoiceIds = invoices.map(i => i.id);
    const vendorIds = [...new Set(invoices.map(getVendorId))];

    // Monthly caps are evaluated per the invoice's OWN sell_date month, so fetch
    // approved aggregates across the FULL span of months present in this batch
    // (a re-reconcile sweep covers many months at once).
    const months = invoices
      .map(i => (i.sell_date ? monthOf(i.sell_date) : null))
      .filter((x): x is string => !!x)
      .sort(); // "YYYY-MM" sorts chronologically as plain strings
    const fallbackMonth = monthOf(new Date().toISOString());
    const minMonth = months[0] ?? fallbackMonth;
    const maxMonth = months[months.length - 1] ?? fallbackMonth;
    const startDate = `${minMonth}-01`;
    const endDate = firstOfNextMonth(maxMonth); // exclusive upper bound

    const [lineItemsMap, monthlyAggs, warningClauses] = await Promise.all([
      this.dao.getLineItemsForInvoices(invoiceIds),
      this.dao.getMonthlyAggregates(vendorIds, startDate, endDate),
      this.dao.getWarningClauses(),
    ]);

    console.log(`[auto-approve] ${invoices.length} invoices, ${rules.length} rules, ${vendorIds.length} vendors, months=${minMonth}..${maxMonth}`);

    // Running per-(vendor, sell-month) tallies, seeded from already-approved baselines.
    const batchCounts = new Map<string, MonthlyAggregates>();
    for (const [key, agg] of monthlyAggs) batchCounts.set(key, { ...agg });

    const summary: ApprovalResult = { approved: 0, suggested: 0, skipped: 0, failed: 0 };

    for (const inv of invoices) {
      const vendorId = getVendorId(inv);
      // require_paid is evaluated inside findMatchingRule: a paid-only rule is skipped for an
      // unpaid invoice so matching falls through to the next (e.g. capped) rule for that vendor.
      const rule = InvoiceApprovalService.findMatchingRule(rules, vendorId, inv.currency, isPaid(inv));

      if (!rule) {
        console.log(`[auto-approve] invoice ${inv.id}: no matching rule — skipped`);
        summary.skipped++;
        continue;
      }

      const lineItems = (lineItemsMap.get(inv.id) ?? []) as { name: string; description: string | null; vat_rate_code: string | null }[];
      const bucketKey = `${vendorId}:${inv.sell_date ? monthOf(inv.sell_date) : minMonth}`;
      const currentAgg = batchCounts.get(bucketKey) ?? { count: 0, totalNet: 0 };
      const evalResult = InvoiceApprovalService.evaluateRule(rule, inv, lineItems, currentAgg);

      // Global warning-clause dictionary — a match blocks auto-approval outright,
      // independent of which rule matched. Scans line item text (the same
      // `name || description` the expected_line_item_descriptions check reads)
      // plus the invoice notes.
      const matchedClauses = findWarningClauses(warningClauses, [
        // BOTH fields, not `name || description`: `name` is NOT NULL and in
        // practice never blank, so the `||` form the rule-level
        // expected_line_item_descriptions check uses would make `description`
        // dead — and a clause hiding in the description is exactly the case
        // this dictionary exists to catch.
        ...lineItems.flatMap(li => [li.name, li.description]),
        inv.notes,
      ]);

      let reportResult: 'auto_approved' | 'suggest_approval' | 'partial';
      const update: Parameters<InvoiceDao['updateStatus']>[1] = {};

      if (matchedClauses.length > 0) {
        reportResult = 'partial';
        summary.failed++;
        console.log(`[auto-approve] invoice ${inv.id}: SUSPICIOUS — clauses: ${matchedClauses.map(c => c.phrase).join(', ')}`);
      } else if (!evalResult.allowed) {
        reportResult = 'partial';
        summary.failed++;
        console.log(`[auto-approve] invoice ${inv.id}: FAILED rule "${rule.name}" — ${evalResult.failedChecks.join(', ')}`);
      } else if (rule.action === 'auto_approve') {
        reportResult = 'auto_approved';
        update.review_status = 'approved';
        summary.approved++;
        console.log(`[auto-approve] invoice ${inv.id}: APPROVED by rule "${rule.name}"`);
      } else {
        reportResult = 'suggest_approval';
        summary.suggested++;
        console.log(`[auto-approve] invoice ${inv.id}: SUGGESTED by rule "${rule.name}"`);
      }

      if (rule.override_payment_method) update.payment_method = rule.override_payment_method;
      const clauseViolations: ReconcileViolation[] = matchedClauses.length > 0
        ? [{ type: 'suspicious_clause', details: { phrases: matchedClauses.map(c => c.phrase) } }]
        : [];
      // buildApprovalReport deliberately emits an approval-only subset of ReconcileReport
      // (no bank_check key) — asserted rather than widened so the persisted JSON is unchanged.
      update.reconcile_report = buildApprovalReport(reportResult, {
        ruleId: rule.id, ruleName: rule.name,
        checks: evalResult.checks, failedChecks: evalResult.failedChecks,
      }, clauseViolations) as ReconcileReport;

      await this.dao.updateStatus(inv.id, update, { invalidate: false });

      if (reportResult === 'auto_approved' || reportResult === 'suggest_approval') {
        currentAgg.count++;
        currentAgg.totalNet += Math.abs(inv.net_amount ?? 0);
        batchCounts.set(bucketKey, currentAgg);
      }
    }

    console.log(`[auto-approve] done: approved=${summary.approved} suggested=${summary.suggested} skipped=${summary.skipped} failed=${summary.failed}`);
    return summary;
  }
}

// Existing importers (tests, edge fns) keep resolving these from the module; the
// implementations now live on the class per the class-based Service/DAO rule.
export const findMatchingRule = InvoiceApprovalService.findMatchingRule;
export const evaluateRule = InvoiceApprovalService.evaluateRule;
